import { PocTooltip } from 'commons/components/components-wrapper/poc-tooltip.component';
import type { PocInputValidity } from 'commons/types';
import { Select, SelectOption, SelectProps, SelectPropsValue, TooltipedIconComponent } from 'commons/components';
import { useTranslation } from 'core/i18n/i18n';
import { useEffect, useState } from 'react';
import { IdentityProviderMapping } from 'modules/settings/pods/settings-integrations/settings-integrations-active-directory/types/active-directory.type';
import { InputDataPoint, SelectDataPoint } from './components';
import { DataPointMappingOptions, DataPointMappingUnion } from 'modules/settings/pods/settings-integrations/components';
import { DataPointMappingLayout } from './data-point-mapping.style';
import { getCustomMessages } from './data-point-mapping.validation';

interface DataPointMappingComponentProps {
    identityProviderIndex: number;
    pocFieldInputType: 'select' | 'input';
    pocFieldInputName: string;
    pocFieldInputValue: string;
    pocFieldInputOptions?: SelectOption[];
    pocFieldInputDisabled?: boolean;
    ldapFieldInputName: string;
    ldapFieldInputValue?: string;
    ldapPlaceholder?: string;
    ldapFieldInputOptions?: SelectProps['options'];
    ldapFieldInputDisabled?: boolean;
    identityProviderMapping?: IdentityProviderMapping[];
    showTooltip?: boolean;
    tooltipText?: string;
    onChangeIdentityProviderMapping: <K extends keyof IdentityProviderMapping>(
        index: number,
        field: K,
        value: IdentityProviderMapping[K]
    ) => void;
    onRemove?: (field: string) => void;
}

const mappingError: PocInputValidity = { state: 'warning', type: ['custom'] };

export const DataPointMappingComponent: React.FC<DataPointMappingComponentProps> = (props) => {
    const {
        identityProviderIndex,
        pocFieldInputType,
        pocFieldInputName,
        pocFieldInputValue,
        pocFieldInputOptions = [],
        pocFieldInputDisabled = false,
        ldapFieldInputName,
        ldapFieldInputValue = '',
        ldapPlaceholder,
        ldapFieldInputOptions = [],
        ldapFieldInputDisabled = false,
        identityProviderMapping = [],
        showTooltip = false,
        tooltipText = '',
        onRemove,
        onChangeIdentityProviderMapping,
    } = props;

    const [ldapInputValidity, setLdapInputValidity] = useState<PocInputValidity | undefined>(undefined);
    const [pocFieldInputSelectValidity, setPocFieldInputSelectValidity] = useState<PocInputValidity | undefined>(
        undefined
    );
    const { t } = useTranslation();

    const [ldapOptions, setLdapOptions] = useState<SelectOption[]>(ldapFieldInputOptions);
    const [pocInputOptions, setPocInputOptions] = useState<SelectOption[]>(pocFieldInputOptions);

    useEffect(() => {
        if (checkLdapMappingValidity()) {
            setLdapInputValidity(mappingError);
            setLdapOptions([
                ...ldapFieldInputOptions,
                { text: ldapFieldInputValue, value: ldapFieldInputValue, disabled: true },
            ]);
        }
    }, [ldapFieldInputOptions]);

    useEffect(() => {
        if (pocFieldInputOptions.length) {
            const newOptions: SelectOption[] = pocFieldInputOptions.filter(
                (pf) =>
                    !identityProviderMapping.map((ipm) => ipm.operatorField).includes(String(pf.value)) ||
                    pf.value === pocFieldInputValue
            );

            if (checkInputOptionsValidity()) {
                setPocFieldInputSelectValidity(mappingError);

                const additionalOption = {
                    text:
                        pocFieldInputOptions?.find((f) => f.value === pocFieldInputValue)?.text ??
                        t(
                            'settings.integrations.active-directory.data-points.mapping.poc.optional.fields.non-existing'
                        ),
                    value: pocFieldInputValue,
                    disabled: true,
                };

                newOptions.push(additionalOption);
            }
            setPocInputOptions((prevOptions) =>
                JSON.stringify(prevOptions) === JSON.stringify(newOptions) ? prevOptions : newOptions
            );
        }
    }, [pocFieldInputOptions, identityProviderMapping]);

    const checkLdapMappingValidity = () =>
        !ldapFieldInputOptions?.some((option) => option.value === ldapFieldInputValue) &&
        ldapFieldInputValue.length > 0;

    const checkInputOptionsValidity = () =>
        !pocFieldInputOptions?.some((option) => option.value === pocFieldInputValue) && pocFieldInputValue.length > 0;

    const handleOnChangeIdentityProviderMapping = (_field: string, value: SelectPropsValue) => {
        onChangeIdentityProviderMapping(identityProviderIndex, 'identityProviderField', value as string);
        if (checkLdapMappingValidity()) {
            setLdapInputValidity({ state: 'valid' });
        }
    };

    return (
        <DataPointMappingLayout>
            {pocFieldInputType === 'input' ? (
                <InputDataPoint
                    pocFieldInputName={pocFieldInputName}
                    pocFieldInputValue={pocFieldInputValue}
                    pocFieldInputDisabled={pocFieldInputDisabled}
                    identityProviderIndex={identityProviderIndex}
                    onChangeIdentityProviderMapping={onChangeIdentityProviderMapping}
                />
            ) : undefined}
            {pocFieldInputType === 'select' ? (
                <SelectDataPoint
                    pocFieldInputName={pocFieldInputName}
                    pocFieldInputValue={pocFieldInputValue}
                    pocFieldInputDisabled={pocFieldInputDisabled}
                    pocInputOptions={pocInputOptions}
                    pocFieldInputSelectValidity={pocFieldInputSelectValidity}
                    pocFieldInputOptions={pocFieldInputOptions}
                    identityProviderIndex={identityProviderIndex}
                    validationMessages={getCustomMessages(t).optional.pocField}
                    setPocFieldInputSelectValidity={setPocFieldInputSelectValidity}
                    onChangeIdentityProviderMapping={onChangeIdentityProviderMapping}
                />
            ) : undefined}
            {showTooltip ? (
                <PocTooltip anchor={pocFieldInputName} placement="top">
                    {tooltipText}
                </PocTooltip>
            ) : undefined}
            <DataPointMappingUnion />
            <Select
                className="without-label"
                name={ldapFieldInputName}
                placeholder={
                    ldapPlaceholder ?? 'settings.integrations.active-directory.data-points.mapping.ldap.placeholder'
                }
                value={ldapFieldInputValue}
                options={ldapOptions}
                onChange={(field, value) => handleOnChangeIdentityProviderMapping(field, (value as string[])[0])}
                disabled={ldapFieldInputDisabled}
                customValidationCode="mapping"
                validationMessages={getCustomMessages(t)[pocFieldInputDisabled ? 'mandatory' : 'optional'].ldapField}
                validity={ldapInputValidity}
                onValidity={(_field, value) => {
                    if (ldapFieldInputOptions?.some((option) => option.value === ldapFieldInputValue)) {
                        setLdapInputValidity(value);
                    }
                }}
                customKeyItemPrefix={`ldapMappingLdapField${identityProviderIndex}`}
            />
            {showTooltip ? (
                <PocTooltip anchor={ldapFieldInputName} placement="top">
                    {tooltipText}
                </PocTooltip>
            ) : undefined}
            <DataPointMappingOptions>
                {onRemove ? (
                    <TooltipedIconComponent
                        icon="delete"
                        tooltipText={t('settings.integrations.active-directory.data-points.mapping.optional.delete')}
                        family="filled"
                        onClick={() => onRemove(pocFieldInputName)}
                    />
                ) : undefined}
            </DataPointMappingOptions>
        </DataPointMappingLayout>
    );
};
